import type {MouseEvent, MouseEventHandler, ReactElement} from 'react';
import {useMemo, useRef} from 'react';
import {ClickableDiv} from '@Components/clickable-div';
import type {PillButtonDropdownItems, PillButtonImageProps} from '@Components/pill-button/pill-button.types';
import {PillButtonSize} from '@Components/pill-button/pill-button.types';
import {Text, TextSize, TextType} from '@Components/text';
import {CircularProgressLoader, LOADER_COLOR, LOADER_SIZE} from '@Components/circular-progress-loader';
import type {IconProps} from '@Components/icon-v2';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {Dropdown} from '@Components/dropdown-v2';
import {ALIGNMENT_TYPE, DROPDOWN_POSITION} from '@Components/dropdown-v2/dropdown.types';
import type {ControlledListItem3} from '@Components/controlled-list/controlled-list.types';
import {CONTROLLED_LIST_ITEM_TYPE} from '@Components/controlled-list/controlled-list.types';
import {ControlledList} from '@Components/controlled-list';
import {getUniqueString} from '@Utils/string.util';
import styles from './pill-button.module.scss';
import useDefaultPanelClose from '@Panels/hooks/useDefaultPanelClose';

interface PillButtonProps {
  size: PillButtonSize;
  text: string;
  isSelected?: boolean;
  dropDownItems?: PillButtonDropdownItems[];
  selectedDropDownItemID?: string;
  retainSizeOnSelection?: boolean;
  isLoading?: boolean;
  onClose?: MouseEventHandler;
  isMobile?: boolean;
  dropDownClasses?: string;
  isDisabled?: boolean;
  className?: string;
  leftIconProps?: IconProps;
  leftImageProps?: PillButtonImageProps;
  fireOnClickWhenSelected?: boolean;
  customContent?: ReactElement;
  textSize?: TextSize;
  textClasses?: string;

  onClick(id?: string, event?: MouseEvent): void;
}

const panelId = 'pill-button-dropdown-panel';
const DEFAULT_IMAGE_SIZE = 16;

export function PillButton({
  isSelected = false,
  isLoading = false,
  isMobile = false,
  isDisabled = false,
  fireOnClickWhenSelected = false,
  textSize = TextSize.XSMALL,
  ...props
}: PillButtonProps): ReactElement<HTMLDivElement> {
  const id = useRef<string>(getUniqueString());
  const onClose = useDefaultPanelClose(panelId);
  const selectedState = useMemo(() => {
    if (props.selectedDropDownItemID) {
      return props.dropDownItems?.some((item) => item.id === props.selectedDropDownItemID) ?? false;
    }
    return isSelected;
  }, [props.selectedDropDownItemID, props.dropDownItems, isSelected]);

  const getPaddingX = (): string => {
    if (selectedState && !props.retainSizeOnSelection) {
      if (props.size === PillButtonSize.MEDIUM) {
        return props.onClose ? styles.selectedMediumPadding : styles.selectionMediumPaddingWithoutCross;
      }
      return styles.selectedSmallPadding;
    }
    if (props.size === PillButtonSize.MEDIUM) {
      return props.dropDownItems && props.dropDownItems.length > 0 ? styles.dropdownPillMediumPadding : styles.unSelectedMediumPadding;
    }
    return styles.unSelectedSmallPadding;
  };

  const getStylesForContainer = (): string => {
    return `${isDisabled ? '_cursor-not-allowed' : '_cursor-pointer'} hover-transition-all ${
      styles.containerPadding
    } ${getPaddingX()} radius-round _fit-width ${getColorClasses()} ${getStyleForSize()} ${getBorderStyle()} ${props.className ?? ''}`;
  };

  const getColorClasses = (): string => {
    if (isDisabled) {
      return styles.disabledContainer;
    }
    return selectedState ? styles.selectedContainer : styles.container;
  };

  const getBorderStyle = (): string => {
    if (isDisabled) {
      return '';
    }
    return selectedState ? styles.selectedBorder : 'border-s-darker';
  };

  const getStyleForSize = (): string => {
    switch (props.size) {
      case PillButtonSize.MEDIUM:
        return styles.sizeM;
      case PillButtonSize.SMALL:
        return styles.sizeS;
      default:
        throw new Error(`Unhanlded size for style: ${props.size}`);
    }
  };

  const renderLeftImage = (): ReactElement | null => {
    if (props.leftIconProps || !props.leftImageProps) {
      return null;
    }

    const size = props.leftImageProps.size ?? DEFAULT_IMAGE_SIZE;
    return <img src={props.leftImageProps.src} alt={props.leftImageProps.alt ?? ''} width={size} height={size} />;
  };

  const getSelectedState = (): ReactElement<HTMLElement> => {
    let selectedText = props.text;
    if (props.selectedDropDownItemID && props.dropDownItems) {
      selectedText =
        props.dropDownItems.find((item) => {
          return item.id === props.selectedDropDownItemID;
        })?.text ?? props.text;
    }

    return (
      <>
        <div className={`flex-center ${styles.gap4}`}>
          {props.leftIconProps ? (
            <Icon {...props.leftIconProps} className={props.leftIconProps.className ?? `${props.leftIconProps.className} ${isSelected ? styles.selectedIcon : ''}`} />
          ) : null}
          {renderLeftImage()}
          {props.customContent ?? (
            <Text disableSelection bold val={selectedText} size={textSize} type={TextType.BODY} className={`content-primary ${styles.pillText} ${props.textClasses ?? ''}`} />
          )}
        </div>
        {getCloseIcon()}
      </>
    );
  };

  const getDefaultState = (): ReactElement<HTMLDivElement> => {
    return isLoading ? (
      <CircularProgressLoader size={LOADER_SIZE.XSMALL} color={LOADER_COLOR.DARK} />
    ) : (
      <>
        <div className={`flex-center ${styles.gap4}`}>
          {props.leftIconProps ? <Icon {...props.leftIconProps} /> : null}
          {renderLeftImage()}
          {props.customContent ?? (
            <Text disableSelection bold val={props.text} size={textSize} type={TextType.BODY} className={`content-body ${styles.pillText} ${props.textClasses ?? ''}`} />
          )}
        </div>
        {getCloseIcon()}
        {getCaretDownIcon()}
      </>
    );
  };

  const getCaretDownIcon = (): ReactElement<HTMLElement> | null => {
    return props.dropDownItems && props.dropDownItems.length > 0 ? <Icon size={IconSize.SIZE_ICON_16} icon="icon-caret-down" className="content-body" /> : null;
  };

  const getCloseIcon = (): ReactElement<HTMLElement> | null => {
    return props.onClose && selectedState ? (
      <Icon
        size={IconSize.SIZE_ICON_16}
        icon="icon-close"
        className="content-primary"
        onClick={(e): void => {
          if (props.onClose && !isDisabled) {
            props.onClose(e);
          }
        }}
      />
    ) : null;
  };

  const getContentMarkup = (): ReactElement<HTMLElement> => {
    return selectedState && !isLoading ? getSelectedState() : getDefaultState();
  };

  const getSelector = (): ReactElement<HTMLDivElement> => {
    return (
      <ClickableDiv
        className={getStylesForContainer()}
        onClick={(event): void => {
          if ((!selectedState && !isDisabled) || fireOnClickWhenSelected) {
            props.onClick(undefined, event);
          }
        }}
      >
        <div className={`flex-center _full-width _full-height ${props.dropDownItems && props.dropDownItems.length > 0 ? styles.gap6 : styles.gap16}`}>{getContentMarkup()}</div>
      </ClickableDiv>
    );
  };

  const getDropDownList = (): ReactElement<HTMLDivElement> => {
    const listItems: ControlledListItem3[] | undefined = props.dropDownItems?.map((item) => {
      return {
        id: item.id,
        text: item.text,
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_3,
        isSelected: props.selectedDropDownItemID ? item.id === props.selectedDropDownItemID : false,
      };
    });

    return (
      <div className="flex-column-justify-center">
        <ControlledList
          className={`spacing-p-b-0 spacing-p-r-2 spacing-p-l-2 spacing-p-t-2 spacing-m-0 ${props.dropDownClasses ?? ''}`}
          id={`${id.current}-dropdown-list`}
          onClick={(listItemId, e): void => {
            if (!isDisabled) {
              props.onClick(listItemId, e);
              onClose();
            }
          }}
          items={listItems ?? []}
        />
      </div>
    );
  };

  const getDropdown = (): ReactElement<HTMLDivElement> => {
    return (
      <Dropdown
        position={DROPDOWN_POSITION.FIXED}
        alignment={ALIGNMENT_TYPE.BOTTOM_START}
        popUpHasCustomWidth
        selector={getSelector()}
        popup={getDropDownList()}
        popupSpacingClass="spacing-p-0"
        showModalForSmallScreen={isMobile}
        modalTitle={props.text}
        panelIdForModal={panelId}
      />
    );
  };

  return props.dropDownItems && props.dropDownItems.length > 0 ? getDropdown() : getSelector();
}
